import type {CountryCode} from "libphonenumber-js";
import type {ExternalBulkContactInput} from "../../store/features/Contacts/contactsService";
import type {ContactImportParseResult} from "./contactImportTypes";
import {getBrowserCountryCode, normalizeImportedPhoneNumber} from "../../utilis/msisdnUtilis";

/**
 * RFC 4180-ish CSV parser. Handles quoted fields, escaped quotes ("" → "),
 * and CR/LF/CRLF line endings. Returns rows of string cells.
 */
export function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let inQuotes = false;

    // Strip BOM if present.
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else inQuotes = false;
            } else {
                field += c;
            }
        } else {
            if (c === '"') {
                inQuotes = true;
            } else if (c === ",") {
                row.push(field);
                field = "";
            } else if (c === "\r" || c === "\n") {
                if (c === "\r" && text[i + 1] === "\n") i++;
                row.push(field);
                field = "";
                if (row.length > 1 || row[0] !== "") rows.push(row);
                row = [];
            } else {
                field += c;
            }
        }
    }
    if (field !== "" || row.length > 0) {
        row.push(field);
        if (row.length > 1 || row[0] !== "") rows.push(row);
    }
    return rows;
}

/**
 * Map Google Contacts CSV rows to backend `ExternalBulkContactInput`.
 * - Pulls all `Phone N - Value` columns; first valid becomes main.
 * - Falls back to "Organization Name" for the display name if first/last are blank.
 * - Skips rows without any valid phone number.
 */
export function googleCsvToExternalContacts(
    csvText: string,
    ownerId: string,
    defaultCountry: CountryCode = getBrowserCountryCode(),
): ExternalBulkContactInput[] {
    return parseGoogleCsvContacts(csvText, ownerId, defaultCountry).contacts;
}

export function parseGoogleCsvContacts(
    csvText: string,
    ownerId: string,
    defaultCountry: CountryCode = getBrowserCountryCode(),
): ContactImportParseResult {
    const rows = parseCsv(csvText);
    if (rows.length < 2) {
        return {
            contacts: [],
            failures: [{
                id: "csv-file",
                name: "CSV file",
                phoneNumbers: [],
                messages: ["The file does not contain any contact rows."],
            }],
        };
    }

    const headers = rows[0];
    const idx = (name: string) => headers.findIndex(h => h.trim().toLowerCase() === name.toLowerCase());

    const firstIdx = idx("First Name");
    const lastIdx = idx("Last Name");
    const orgIdx = idx("Organization Name");
    const emailIdxes = headers
        .map((h, i) => (/^E-?mail \d+ - Value$/i.test(h.trim()) ? i : -1))
        .filter(i => i >= 0);
    const phoneIdxes = headers
        .map((h, i) => (/^Phone \d+ - Value$/i.test(h.trim()) ? i : -1))
        .filter(i => i >= 0);

    const contacts: ExternalBulkContactInput[] = [];
    const failures: ContactImportParseResult["failures"] = [];

    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const firstName = firstIdx >= 0 ? row[firstIdx]?.trim() : "";
        const lastName = lastIdx >= 0 ? row[lastIdx]?.trim() : "";
        const company = orgIdx >= 0 ? row[orgIdx]?.trim() : "";
        const email = emailIdxes
            .map(i => row[i]?.trim())
            .find(Boolean);

        const rawPhoneNumbers = phoneIdxes
            .flatMap(i => splitGoogleContactValues(row[i] ?? ""))
            .filter(Boolean);
        const normalizedPhoneNumbers = rawPhoneNumbers.map(raw => ({
            raw,
            normalized: normalizeImportedPhoneNumber(raw, defaultCountry),
        }));
        const invalidPhoneNumbers = normalizedPhoneNumbers
            .filter(phone => !phone.normalized)
            .map(phone => phone.raw);
        const phoneNumbers = normalizedPhoneNumbers
            .map(phone => phone.normalized)
            .filter((phone): phone is string => !!phone);
        const name = [firstName, lastName].filter(Boolean).join(" ") || company || "Unnamed contact";
        const messages: string[] = [];

        if (rawPhoneNumbers.length === 0) {
            messages.push("No phone number was provided.");
        } else if (invalidPhoneNumbers.length > 0) {
            messages.push(`Invalid phone number${invalidPhoneNumbers.length === 1 ? "" : "s"}: ${invalidPhoneNumbers.join(", ")}.`);
        }
        if (!firstName && !lastName && !company) {
            messages.push("A name or company is required.");
        }
        if (messages.length > 0) {
            failures.push({
                id: `csv-row-${r + 1}`,
                name,
                phoneNumbers: rawPhoneNumbers,
                messages,
            });
            continue;
        }

        contacts.push({
            ownerId,
            firstName: firstName || company || undefined,
            lastName: lastName || undefined,
            email: email || undefined,
            company: company || undefined,
            availability: "PRIVATE",
            phoneNumbers: phoneNumbers.map((phoneNumber, i) => ({
                phoneNumber,
                main: i === 0,
            })),
            source: "WEB_CSV_IMPORT",
            externalIdentifier: {
                id: hashContact(firstName, lastName, phoneNumbers[0]),
                name: "web_csv_import",
            },
        });
    }

    return {contacts, failures};
}

/**
 * Google Contacts joins multiple values stored in one CSV cell with ` ::: `.
 * Keep support for manually authored comma/semicolon-separated phone lists.
 */
function splitGoogleContactValues(value: string): string[] {
    return value
        .split(/\s*:::\s*|[;,]/)
        .map(part => part.trim())
        .filter(Boolean);
}

/** Stable id for dedup — recordID-equivalent on the backend. */
function hashContact(firstName: string, lastName: string, msisdn: string): string {
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}|${msisdn}`;
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return `csv-${hash.toString(16)}`;
}
